import { useCallback, useState, type ReactNode } from 'react';
import {
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import SubscribeButton from '../components/SubscribeButton';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `podcastSubscribe` and `share.*` slots consumed below.
import { brandingFixture } from '../lib/branding/branding-fixture';
import type { PodcastSubscribeUrls } from '@dvgup/contracts/branding-config';

/**
 * Podcast subscribe info page (task 9.1, public-website spec).
 *
 * `<PodcastPage>` at route `/podcast` renders the seven subscribe
 * surfaces in the order required by **Requirement 4.1**: Apple
 * Podcasts, Spotify, Amazon Music, YouTube Music, Pocket Casts,
 * Overcast, RSS feed. Each platform's destination URL is sourced
 * from `BrandingConfig.podcastSubscribe.<platform>` per
 * **Requirement 4.2**:
 *
 *   - When the URL is present and non-empty, the surface renders as
 *     an MUI `<Button>` with `href={url}`,
 *     `target="_blank" rel="noopener noreferrer"` so a click opens
 *     the platform's subscribe page in a new tab.
 *   - When the URL is absent or empty, the surface renders as a
 *     **disabled** MUI `<Button>` wrapped in a `<Tooltip>` showing
 *     "subscribe link coming soon". The disabled button has no
 *     `href` and does not navigate.
 *
 * The branch logic above lives in the presentational
 * `<SubscribeButton>` component (task 9.2); this page wires the
 * per-platform `label`, `url`, and `icon` props. Platform icon
 * choices use MUI's icon set (`@mui/icons-material@^9`):
 *
 *   - `applePodcasts`, `pocketCasts`, `overcast` → `<PodcastsIcon>`
 *   - `spotify`, `amazonMusic`, `youtubeMusic`   → `<MusicNoteIcon>`
 *   - `rssFeed`                                  → `<RssFeedIcon>`
 *
 * Per **Requirement 4.3**, the RSS feed URL is **also** rendered as
 * a read-only MUI `<TextField>` with a copy-to-clipboard
 * `<IconButton>` adornment so visitors using podcast apps not in
 * the listed set can subscribe by pasting the feed URL into their
 * client. The copy button calls `navigator.clipboard.writeText(...)`
 * and surfaces a transient "Copied" tooltip; visitors using
 * keyboard navigation pick up the same MUI focus ring as every
 * other interactive element on the page (Requirement 11.2).
 *
 * The page reads its branding tokens from the placeholder
 * `brandingFixture` import until `@dvgup/branding` lands per the
 * `lib/branding/branding-fixture.ts` migration note. Function-
 * length budget: each helper component below stays under 20 lines
 * per Requirement 3.7.
 *
 * _Validates: Requirement 4.1 (seven platforms in listed order),
 * Requirement 4.2 (absent URL → disabled + tooltip; present URL →
 * new-tab link), Requirement 4.3 (RSS field with copy adornment),
 * R8.7 (branding-token sourcing via `BrandingConfig`)._
 */

/** Stable ordered list of the seven subscribe platforms per Requirement 4.1. */
type PlatformKey = keyof PodcastSubscribeUrls;

interface PlatformEntry {
  readonly key: PlatformKey;
  readonly label: string;
  readonly icon: ReactNode;
}

const PLATFORM_ENTRIES: readonly PlatformEntry[] = [
  { key: 'applePodcasts', label: 'Apple Podcasts', icon: <PodcastsIcon /> },
  { key: 'spotify', label: 'Spotify', icon: <MusicNoteIcon /> },
  { key: 'amazonMusic', label: 'Amazon Music', icon: <MusicNoteIcon /> },
  { key: 'youtubeMusic', label: 'YouTube Music', icon: <MusicNoteIcon /> },
  { key: 'pocketCasts', label: 'Pocket Casts', icon: <PodcastsIcon /> },
  { key: 'overcast', label: 'Overcast', icon: <PodcastsIcon /> },
  { key: 'rssFeed', label: 'RSS feed', icon: <RssFeedIcon /> },
];

interface RssCopyFieldProps {
  readonly url: string;
}

/**
 * Read-only MUI `<TextField>` rendering the RSS feed URL with a
 * copy-to-clipboard `<IconButton>` adornment per Requirement 4.3.
 */
function RssCopyField({ url }: RssCopyFieldProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
    });
  }, [url]);
  return (
    <TextField
      label="RSS feed URL"
      value={url}
      fullWidth
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={copied ? 'Copied' : 'Copy RSS feed URL'}>
                <IconButton
                  onClick={handleCopy}
                  edge="end"
                  aria-label="Copy RSS feed URL"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

/**
 * `<PodcastPage>` — route `/podcast` per Requirement 4. See the file
 * header for the full contract.
 */
export default function PodcastPage(): JSX.Element {
  const subscribe = brandingFixture.podcastSubscribe ?? {};
  const rssUrl = subscribe.rssFeed;
  return (
    <Card sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/podcast"
          pageTitle="Podcast"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 1 }}>
          Subscribe to the DVGUP Podcast
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          {brandingFixture.copy.podcastSubtitle}
        </Typography>
        <Stack spacing={2}>
          {PLATFORM_ENTRIES.map((entry) => (
            <SubscribeButton
              key={entry.key}
              label={entry.label}
              url={subscribe[entry.key]}
              icon={entry.icon}
            />
          ))}
          {rssUrl !== undefined && rssUrl !== '' && (
            <RssCopyField url={rssUrl} />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
